import React, { useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import { useCancelarNFe } from '../../../../hooks/useNFe';
import { formatDateTimeBrazil } from '../../../../utils/dateUtils';

interface ModalCancelarNFeProps {
  emissaoId: string;
  dhRecbto?: string;
}

const ModalCancelarNFe: React.FC<ModalCancelarNFeProps> = ({ emissaoId, dhRecbto }) => {
  const [show, setShow] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');

  const { showToast } = useToast();
  const cancelarMutation = useCancelarNFe(emissaoId);

  const prazoExpiracao = dhRecbto
    ? new Date(new Date(dhRecbto).getTime() + 24 * 60 * 60 * 1000)
    : null;
  const minutosRestantes = prazoExpiracao
    ? Math.floor((prazoExpiracao.getTime() - Date.now()) / 60000)
    : null;
  const prazoCritico = minutosRestantes !== null && minutosRestantes < 120;

  const handleConfirmar = () => {
    if (justificativa.length < 15) {
      setErro('Justificativa deve ter pelo menos 15 caracteres');
      return;
    }
    setErro('');
    cancelarMutation.mutate(
      { justificativa },
      {
        onSuccess: () => {
          showToast('NF-e cancelada com sucesso.', 'success');
          setShow(false);
          setJustificativa('');
        },
        onError: (error: any) => {
          setErro(error?.response?.data?.message ?? 'Erro ao cancelar NF-e');
        },
      }
    );
  };

  const handleFechar = () => {
    if (!cancelarMutation.isPending) {
      setShow(false);
    }
  };

  return (
    <>
      <Button variant="phoenix-danger" size="sm" onClick={() => setShow(true)}>
        <FontAwesomeIcon icon={faBan} className="me-1" /> Cancelar NF-e
      </Button>

      <Modal show={show} onHide={handleFechar}>
        <Modal.Header closeButton>
          <Modal.Title>Cancelar NF-e</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {prazoCritico && prazoExpiracao && (
            <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle">
              <div className="text-warning-emphasis">
                <strong>Atenção:</strong> Restam apenas {minutosRestantes} minutos para o prazo de
                cancelamento (24h após autorização, expira em{' '}
                {formatDateTimeBrazil(prazoExpiracao.toISOString())}).
              </div>
            </Alert>
          )}

          <Form.Group>
            <Form.Label>Justificativa do cancelamento *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Mínimo 15, máximo 255 caracteres"
              maxLength={255}
              isInvalid={!!erro}
            />
            <Form.Text className="text-body-secondary">
              {justificativa.length}/255 caracteres
            </Form.Text>
            {erro && <Form.Control.Feedback type="invalid">{erro}</Form.Control.Feedback>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="subtle-secondary" onClick={handleFechar} disabled={cancelarMutation.isPending}>
            Fechar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmar}
            loading={cancelarMutation.isPending}
          >
            Confirmar Cancelamento
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalCancelarNFe;
